import { create } from "zustand"

import type { FlowStep, FlowStepSetter } from "@/components/helpers/Flow/types"
import { createFlowStepSetter } from "@/components/helpers/Flow/utils"
import type { getStartedFlowSteps } from "@/components/modals/flows/GetStarted/steps"
import type { joinSpaceflowSteps } from "@/components/modals/flows/JoinSpace/steps"
import type { ProSpaceFlowSteps } from "@/components/modals/flows/ProSpace"
import type { AgentDevice } from "@/store/board/agent/AgentClass"

// Remember to import flow descriptors as types, to avoid creating circular dependencies!
// This way we won't leak anything during runtime.
interface ModalStoreState {
  proSpaceFlow: {
    step: FlowStep<typeof ProSpaceFlowSteps> | undefined
  }
  setProSpaceFlowStep: FlowStepSetter<typeof ProSpaceFlowSteps>
  closeProSpaceFlow: () => void
  joinSpaceFlow: {
    step: FlowStep<typeof joinSpaceflowSteps> | undefined
  }
  setJoinSpaceFlowStep: FlowStepSetter<typeof joinSpaceflowSteps>
  closeJoinSpaceFlow: () => void
  getStartedFlow: {
    step: FlowStep<typeof getStartedFlowSteps> | undefined
    store?: {
      board: { name: string; fqbn: string }
      serial: AgentDevice
    }
  }
  setGetStartedFlowStep: FlowStepSetter<typeof getStartedFlowSteps>
  closeGetStartedFlow: () => void
  setGetStartedFlowStore: (store: ModalStoreState["getStartedFlow"]["store"]) => void
}

/**
 * Modal store.
 */
export const useModalStore = create<ModalStoreState>()(set => ({
  proSpaceFlow: {
    step: undefined
  },
  setProSpaceFlowStep: createFlowStepSetter<typeof ProSpaceFlowSteps>(step =>
    set(state => ({ ...state, proSpaceFlow: { step } }))
  ),
  closeProSpaceFlow: () => set(state => ({ ...state, proSpaceFlow: { step: undefined } })),
  joinSpaceFlow: {
    step: undefined
  },
  setJoinSpaceFlowStep: createFlowStepSetter<typeof joinSpaceflowSteps>(step =>
    set(state => ({ ...state, joinSpaceFlow: { step } }))
  ),
  closeJoinSpaceFlow: () => set(state => ({ ...state, joinSpaceFlow: { step: undefined } })),
  getStartedFlow: {
    step: undefined
  },
  setGetStartedFlowStep: createFlowStepSetter<typeof getStartedFlowSteps>(step =>
    set(state => ({ ...state, getStartedFlow: { ...state.getStartedFlow, step } }))
  ),
  // Closing also wipes the flow-scoped store.
  closeGetStartedFlow: () => set(state => ({ ...state, getStartedFlow: { step: undefined, store: undefined } })),
  setGetStartedFlowStore: store =>
    set(state => ({
      ...state,
      getStartedFlow: {
        ...state.getStartedFlow,
        store
      }
    }))
}))
